import Tooltip from '@material-ui/core/Tooltip'
import ghSchema from 'hast-util-sanitize/lib/github.json'
import { Options } from 'hast-util-to-jsx-runtime'
import React from 'react'
import { Fragment, jsx, jsxs } from 'react/jsx-runtime'
import rehypeRaw from 'rehype-raw'
import rehypeReact from 'rehype-react'
import rehypeSanitize from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'

import {
  allowedRenderSchema,
  convertFromMarkdownToHtml,
  convertFromMarkdownToHtmlLineBreaks,
  filterMarkdown,
  rehypeConvertFromBTagToStrongTag,
  rehypeConvertFromITagToEmTag,
  remarkGfmEmphasis,
} from '@karakuri-web/common/markdown-to-text'
import { autoTelLink } from '@karakuri-web/common/remark-auto-tel-link'

const sanitize = { ...ghSchema }
sanitize.protocols?.href.push('tel')

function generateLinkProps(href: string) {
  const rel = 'noopener'
  if (!href.startsWith('tel:') && !href.startsWith('mailto:')) {
    return { href, rel, target: '_blank' }
  }
  return { href, rel }
}

type AnchorProps = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>

export const renderMarkdown = (
  text: string,
  options: {
    hasTooltip: boolean
  } = { hasTooltip: false }
) => {
  function LinkWithTooltip({ children, href = '' }: AnchorProps) {
    return (
      <Tooltip title={href}>
        <a {...generateLinkProps(href)}>{children}</a>
      </Tooltip>
    )
  }
  function LinkWithoutTooltip({ children, href = '' }: AnchorProps) {
    return <a {...generateLinkProps(href)}>{children}</a>
  }
  const convertedMarkdown = convertFromMarkdownToHtmlLineBreaks(text)

  const result = unified()
    .use(remarkParse)
    .use(remarkGfmEmphasis)
    .use(remarkGfm)
    /**
     *  会話カードエディタ下位互換性対応: 非リンク指定のURI Scheme（tel ）を強制的にリンク形式にする.
     *
     *  Markdown形式のリンク構文( "[xxx](xxx)" or <xxx> )で記載した場合は a タグがネストするが仕方がないものとして扱う.
     */
    .use(autoTelLink)
    /** 後続でrehypeSanitize処理を行うため、dangerousHtmlを許可. */
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    /**
     * 会話カードエディタ下位互換性対応: Markdown形式のデータに非サポートのHTMLタグ（b, i）が混入している可能性があるため変換する.
     *
     * 保存されている場合にはサポートしているHTMLタグ（strong, em）に変換する.
     * 編集時にHTMLタグの混入を防止する処理を追加済み.
     */
    .use(rehypeConvertFromBTagToStrongTag)
    .use(rehypeConvertFromITagToEmTag)
    .use(rehypeSanitize, allowedRenderSchema)
    .use(rehypeReact, {
      Fragment: Fragment,
      jsx: jsx,
      jsxs: jsxs,
      components: {
        a: options.hasTooltip ? LinkWithTooltip : LinkWithoutTooltip,
      },
    } as Options)
    .processSync(convertedMarkdown).result
  return result
}

/**
 * 会話カードエディタ下位互換性対応: 旧仕様のテストケース実施・検証用のための関数.
 *
 * Markdownで保存されていたデータをReactComponentで出力する際の前処理で使われていた関数
 */
export function toMarkdown(text: string) {
  return filterMarkdown(text)
}

/**
 * 会話カードエディタ下位互換性対応: 旧仕様のテストケース実施・検証用のための関数.
 *
 * 会話カードエディタMarkdownで保存されていたデータをToastUI Editorに入力する際に使われていた関数.
 */
export function toToast(text: string) {
  return convertFromMarkdownToHtml(text)
}

/**
 * 会話カードエディタ下位互換性対応: 旧仕様のテストケース実施・検証用のための関数.
 *
 * ToastUI Editorから取得したMarkdownを保存前に処理していた関数.
 */
export function formatToast(text: string) {
  return filterMarkdown(text)
}
